import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { PlusCircleIcon } from "@heroicons/react/16/solid";

import { useSidebar } from "../../context/sidebarContext";

import BotSection, { IconBot } from "../dashboard/BotSection";
import CardActiveBot from "../cards/CardActiveBot";
import CardActiveBotSkeleton from "../cards/skeletons/CardActiveBotSkeleton";
import NewBot from "./NewBot";

import "swiper/css";
import "swiper/css/navigation";
import BotLogo from "../commons/BotLogo";
import { useBotDemo } from "../../context/botDemoContext";

const TabButton = ({ activeTab, tabName, onClick, children }) => (
  <div
    className={`flex-1 p-2.5 rounded-md text-sm font-semibold transition-all duration-150 ease-in-out focus:outline-none cursor-pointer text-center ${
      activeTab === tabName
        ? "bg-white text-gray-900 shadow-custom-dual"
        : "text-gray-500 hover:bg-gray-200/70 hover:text-gray-700"
    }`}
    onClick={() => onClick(tabName)}
  >
    {children}
  </div>
);

const BotSwiper = ({ children }) => (
  <div className="flex flex-col md:flex-row md:space-x-4 pb-2">
    <Swiper
      modules={[Navigation]}
      spaceBetween={16}
      slidesPerView={1}
      navigation
      breakpoints={{
        0: { slidesPerView: 1.2 },
        480: { slidesPerView: 1.5 },
        640: { slidesPerView: 2 },
        768: { slidesPerView: 2.2 },
        1024: { slidesPerView: 3 },
        1200: { slidesPerView: 4 },
      }}
      className="md:mt-0 w-full"
    >
      <style>{`
        .swiper-button-next,
        .swiper-button-prev {
          display: none !important;
        }
      `}</style>
      {children}
    </Swiper>
  </div>
);

const ActivateNewBotButton = ({ onClick }) => (
  <div
    className="bg-white/80 backdrop-blur-sm p-3 rounded-xl flex flex-col items-center justify-center border border-dashed border-gray-300 text-center h-[160px] cursor-pointer hover:bg-gray-100"
    style={{ borderDashArray: "2 10" }}
    onClick={onClick}
  >
    <p className="text-base font-medium text-gray-500 mb-2">Activate New Bot</p>
    <div className="flex -space-x-3 mb-3">
      <BotLogo
        iconColor="bg-green-200"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
      <BotLogo
        iconColor="bg-[#FAC482]"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
      <BotLogo
        iconColor="bg-[#B2B7DE]"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
    </div>
    <PlusCircleIcon className="w-6 h-6" />
  </div>
);

export default function ListBotActive() {
  const { isMobile } = useSidebar();
  const {
    strategyLists,
    loadingBotLists,
    botLists,
    selectedBot,
    setSelectedBot,
    setSelectedStrategy,
    setOpenDialogBot,
  } = useBotDemo();
  const [activeTab, setActiveTab] = useState("activeBot");

  const handleBotClick = (bot) => {
    setSelectedBot(bot);
  };

  const handleActivateNewBot = () => {
    setActiveTab("newBot");
  };

  const renderActiveBots = () => {
    if (loadingBotLists) {
      return [...Array(8)].map((_, idx) =>
        isMobile ? (
          <SwiperSlide key={idx}>
            <CardActiveBotSkeleton isBotListPage />
          </SwiperSlide>
        ) : (
          <CardActiveBotSkeleton key={idx + "card-bot"} isBotListPage />
        )
      );
    }

    if (botLists.length) {
      return botLists.map((bot, index) =>
        isMobile ? (
          <SwiperSlide key={index}>
            <CardActiveBot
              bot={bot}
              selectedBot={selectedBot}
              handleBotClick={handleBotClick}
              isBotListPage
            />
          </SwiperSlide>
        ) : (
          <CardActiveBot
            key={bot.id}
            bot={bot}
            selectedBot={selectedBot}
            handleBotClick={handleBotClick}
            isBotListPage
          />
        )
      );
    }

    return null;
  };

  return (
    <div className="w-full bg-white rounded-b-xl flex flex-col">
      <div className="mb-2 p-2 bg-[#F2F2F7] rounded-lg flex m-4 border-[1.2px]">
        <TabButton
          activeTab={activeTab}
          tabName="activeBot"
          onClick={setActiveTab}
        >
          Active Bot
        </TabButton>
        <TabButton
          activeTab={activeTab}
          tabName="newBot"
          onClick={setActiveTab}
        >
          New Bot
        </TabButton>
      </div>

      {isMobile ? (
        <div className="p-4">
          {activeTab === "activeBot" ? (
            <BotSwiper>{renderActiveBots()}</BotSwiper>
          ) : (
            <NewBot
              botLists={strategyLists}
              onActivateBot={(bt) => {
                setSelectedStrategy(bt);
                setOpenDialogBot("step-bot-2");
              }}
            />
          )}
        </div>
      ) : (
        <div
          className={`${
            !botLists.length ? "max-h-[32.75rem]" : "max-h-[80rem]"
          } overflow-y-auto gap-4 p-4 no-scrollbar flex flex-row md:flex-col`}
        >
          {activeTab === "activeBot" ? (
            <>
              {renderActiveBots()}
              {!loadingBotLists && (
                <ActivateNewBotButton onClick={handleActivateNewBot} />
              )}
            </>
          ) : (
            <NewBot
              botLists={strategyLists}
              onActivateBot={(bt) => {
                setSelectedStrategy(bt);
                setOpenDialogBot("step-bot-2");
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
